import { Vec3 } from 'playcanvas';

import { ElementType } from './element';
import type { Scene } from './scene';
import type { Splat } from './splat';
import { State } from './splat-state';

const LAS_SIGNATURE = 0x4c415346; // 'LASF'

function shDcToRgbByte(dc0: number, dc1: number, dc2: number): [number, number, number] {
    const r = Math.round(Math.max(0, Math.min(255, (0.5 + 0.282094792 * dc0) * 255)));
    const g = Math.round(Math.max(0, Math.min(255, (0.5 + 0.282094792 * dc1) * 255)));
    const b = Math.round(Math.max(0, Math.min(255, (0.5 + 0.282094792 * dc2) * 255)));
    return [r, g, b];
}

export type LasApproximatePoint = { x: number; y: number; z: number; r: number; g: number; b: number };

export function collectApproximateSplatCentersWorld(scene: Scene): LasApproximatePoint[] {
    const out: LasApproximatePoint[] = [];
    const tmp = new Vec3();
    const splats = scene.getElementsByType(ElementType.splat) as Splat[];
    for (const splat of splats) {
        const splatData = splat.splatData;
        const xs = splatData.getProp('x') as Float32Array;
        const ys = splatData.getProp('y') as Float32Array;
        const zs = splatData.getProp('z') as Float32Array;
        const rArr = splatData.getProp('f_dc_0') as Float32Array | undefined;
        const gArr = splatData.getProp('f_dc_1') as Float32Array | undefined;
        const bArr = splatData.getProp('f_dc_2') as Float32Array | undefined;
        const state = splatData.getProp('state') as Uint8Array | undefined;
        const n = splat.numSplats;
        const wt = splat.worldTransform;
        for (let i = 0; i < n; i++) {
            if (state && (state[i] & State.deleted)) continue;
            tmp.set(xs[i], ys[i], zs[i]);
            wt.transformPoint(tmp, tmp);
            let r = 128;
            let g = 128;
            let b = 128;
            if (rArr && gArr && bArr) {
                const rgb = shDcToRgbByte(rArr[i], gArr[i], bArr[i]);
                r = rgb[0];
                g = rgb[1];
                b = rgb[2];
            }
            out.push({ x: tmp.x, y: tmp.y, z: tmp.z, r, g, b });
        }
    }
    return out;
}

/** LAS 1.2 / Point Data Record Format 2 (XYZ int32 scaled + RGB ushort). */
export function writeLas12Format2(points: LasApproximatePoint[]): ArrayBuffer {
    const numPoints = points.length;
    const headerSize = 227;
    const pointRecordLength = 26;
    const offsetToPoints = headerSize;
    const totalSize = offsetToPoints + numPoints * pointRecordLength;

    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    for (const p of points) {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        minZ = Math.min(minZ, p.z);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
        maxZ = Math.max(maxZ, p.z);
    }
    if (!Number.isFinite(minX)) {
        minX = minY = minZ = maxX = maxY = maxZ = 0;
    }

    const scale = 0.001;
    const offX = minX;
    const offY = minY;
    const offZ = minZ;

    const buf = new ArrayBuffer(totalSize);
    const dv = new DataView(buf);
    dv.setUint32(0, LAS_SIGNATURE, true);
    dv.setUint16(4, 0, true);
    dv.setUint16(6, 0, true);
    dv.setUint8(24, 1);
    dv.setUint8(25, 2);
    const sys = 'VOLUGRAPH ';
    for (let i = 0; i < sys.length && i < 32; i++) {
        dv.setUint8(26 + i, sys.charCodeAt(i));
    }
    const gen = 'SuperSplat approx splat centers';
    for (let i = 0; i < gen.length && i < 32; i++) {
        dv.setUint8(58 + i, gen.charCodeAt(i));
    }
    const day = new Date();
    const dayOfYear =
        Math.floor(
            (Date.UTC(day.getUTCFullYear(), day.getUTCMonth(), day.getUTCDate()) - Date.UTC(day.getUTCFullYear(), 0, 0)) /
                86400000,
        );
    dv.setUint16(90, dayOfYear, true);
    dv.setUint16(92, day.getUTCFullYear(), true);
    dv.setUint16(94, headerSize, true);
    dv.setUint32(96, offsetToPoints, true);
    dv.setUint32(100, 0, true);
    dv.setUint8(104, 2);
    dv.setUint16(105, pointRecordLength, true);
    dv.setUint32(107, numPoints, true);
    dv.setUint32(111, numPoints, true);
    dv.setUint32(115, 0, true);
    dv.setUint32(119, 0, true);
    dv.setUint32(123, 0, true);
    dv.setUint32(127, 0, true);

    dv.setFloat64(131, scale, true);
    dv.setFloat64(139, scale, true);
    dv.setFloat64(147, scale, true);
    dv.setFloat64(155, offX, true);
    dv.setFloat64(163, offY, true);
    dv.setFloat64(171, offZ, true);
    dv.setFloat64(179, maxX, true);
    dv.setFloat64(187, maxY, true);
    dv.setFloat64(195, maxZ, true);
    dv.setFloat64(203, minX, true);
    dv.setFloat64(211, minY, true);
    dv.setFloat64(219, minZ, true);

    let pos = offsetToPoints;
    for (const p of points) {
        const xi = Math.round((p.x - offX) / scale);
        const yi = Math.round((p.y - offY) / scale);
        const zi = Math.round((p.z - offZ) / scale);
        dv.setInt32(pos, xi, true);
        dv.setInt32(pos + 4, yi, true);
        dv.setInt32(pos + 8, zi, true);
        dv.setUint16(pos + 12, 0, true);
        dv.setUint8(pos + 14, 1);
        dv.setUint8(pos + 15, 0);
        dv.setInt8(pos + 16, 0);
        dv.setUint8(pos + 17, 0);
        dv.setUint16(pos + 18, 0, true);
        dv.setUint16(pos + 20, Math.round((p.r / 255) * 65535), true);
        dv.setUint16(pos + 22, Math.round((p.g / 255) * 65535), true);
        dv.setUint16(pos + 24, Math.round((p.b / 255) * 65535), true);
        pos += pointRecordLength;
    }

    return buf;
}

/** LAZ export deferred — requires WASM laszip (~500KB); flip when product asks. */
export const EXPORT_LAZ_ENABLED = false;
